import { HttpClient } from "@angular/common/http";
import { Component, OnInit, ViewChild } from "@angular/core";

import { Entity, EntityError } from "breeze-client";
import { MenuItem } from "primeng/api";
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from "primeng/dynamicdialog";
import { firstValueFrom } from "rxjs";

import {
  TemplatePreviewDialogComponent,
  TemplatePreviewDialogParams,
} from "./template-preview-dialog.component";

import * as _ from "lodash";
import {
  NotificationEvent,
  NotificationEventEnum,
  NotificationQuery,
  NotificationQueryEnum,
  NotificationTemplate,
  NotificationType,
  NotificationTypeEnum,
  SystemImage,
} from "@/model/entities/entity-model";
import { DateFns } from "@/shared/date-fns";
import { DbQueryService } from "@/shared/db-query.service";
import { DomainService } from "@/shared/domain.service";
import { TemplatesService } from "./templates.service";
import { UtilFns } from "@/shared/util-fns";
import { StringFns } from "@/shared/string-fns";
import { EntityFinderDialog } from "@/shared/entity-finder.dialog";
import { AccountFinderDialogComponent } from "@/admin/account-finder-dialog.component";
import { environment } from "environments/environment";
import { JobOrderFinderDialogComponent } from "@/admin/job-order-finder-dialog.component";
import { ImageHandlerComponent } from "@/shared/image/image-handler.component";
import { AppImage } from "@/shared/image/image-fns";
import { ImageInsertDialogComponent } from "@/shared/editor/image-insert-dialog.component";

export interface NotificationTemplateDialogParams {
  notificationTemplate: NotificationTemplate;
  isNew?: boolean;
  isEditable?: boolean;
}

@Component({
  templateUrl: "./notification-template-dialog.component.html",
  standalone: false,
})
export class NotificationTemplateDialogComponent implements OnInit {
  @ViewChild(ImageHandlerComponent) imageHandler: ImageHandlerComponent;
  UtilFns = UtilFns;

  notificationTemplate!: NotificationTemplate;
  isNew: boolean = false;
  isEditable: boolean = false;
  isFreeForm: boolean = false;

  templates?: NotificationTemplate[];
  contextMenuItems: MenuItem[] = [];
  imageMenuItems: MenuItem[] = [];

  notificationEvents: NotificationEvent[] = [];
  notificationTypes: NotificationType[] = [];
  notificationQueries: NotificationQuery[] = [];
  namedNotificationQuery?: NotificationQuery;
  selectedEvent?: NotificationEvent;

  oldImages: SystemImage[] = [];
  isKeyUnique = true;

  isPageReady = false;
  isBeingAdded!: boolean;
  emailAddress = "";
  phone = "";
  startDate = DateFns.fmtDate4(new Date());

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig<NotificationTemplateDialogParams>,
    private dbQueryService: DbQueryService,
    protected domainService: DomainService,
    private httpClient: HttpClient,
    private templatesService: TemplatesService
  ) {
    // because we pass this function to HtmlEditor
    this.selectImage = this.selectImage.bind(this);
  }

  public static async openDialog(
    dialogService: DialogService,
    data: NotificationTemplateDialogParams,
    config?: DynamicDialogConfig
  ) {
    const ref = dialogService.open(NotificationTemplateDialogComponent, {
      modal: true,
      closable: false,
      draggable: true,
      width: "800px",
      height: "80vh",
      header: "Notification Template Editor",
      focusOnShow: false,
      ...config,
      data: data,
    });
    return firstValueFrom(ref.onClose);
  }

  async ngOnInit() {
    const params = this.config.data;
    UtilFns.assertNonNull(params);

    this.isNew = !!params.isNew;
    this.isEditable = this.isNew || !!params.isEditable;

    this.notificationTemplate = params.notificationTemplate;

    this.isFreeForm =
      this.notificationTemplate.notificationEventId ==
      NotificationEventEnum.FreeForm;

    if (this.isNew) {
      const allTemplates = await this.dbQueryService.getNotificationTemplates(
        this.notificationTemplate.accountId!
      );
      this.templates = allTemplates.filter(
        NotificationTemplate.getTemplateTypeFilter(this.isFreeForm)
      );
      if (this.notificationTemplate.notificationTypeId == null) {
        this.notificationTemplate.notificationTypeId =
          NotificationTypeEnum.InApp;
      }
    }

    [
      this.notificationEvents,
      this.notificationTypes,
      this.notificationQueries,
    ] = await Promise.all([
      this.dbQueryService.getAll(NotificationEvent, true),
      this.dbQueryService.getAll(NotificationType, true),
      this.dbQueryService.getAll(NotificationQuery, true),
      this.loadSystemImages(),
    ]);
    this.notificationEvents = this.notificationEvents.filter(
      (x) => x.id != NotificationEventEnum.FreeForm
    );

    if (
      this.notificationTemplate.notificationEventId ==
      NotificationEventEnum.FreeForm
    ) {
      this.namedNotificationQuery = this.notificationQueries.find(
        (x) => x.name == "AccountUser"
      );
    }

    if (this.isNew && !this.isFreeForm) {
      this.onNotificationEventChange();
    } else {
      this.getContextMenuItems();
      this.getImageMenuItems();
    }

    this.isPageReady = true;

    if (this.isNew) {
      await UtilFns.wait(1);
      this.config.header = this.config.header + " [ New ]";
    }
  }

  isDisabled() {
    return this.notificationTemplate.accountId == null;
  }

  canOk() {
    return (
      this.isKeyUnique &&
      (this.notificationTemplate.notificationEventId != null ||
        this.notificationTemplate.templateName != null) &&
      this.notificationTemplate.notificationTypeId != null
    );
  }

  async onOk() {
    let ok = await this.checkIfUniqueKey();
    if (!ok) return;
    this.notificationTemplate.template = StringFns.purifyWithHandlebars(
      this.notificationTemplate.template
    );
    ok = this.notificationTemplate.entityAspect.validateEntity();
    if (!ok) {
      this.handleBreezeValidationErrors([this.notificationTemplate]);
      return;
    }
    this.ref.close(true);
  }

  async onTemplateNameChange() {
    await this.checkIfUniqueKey();
  }

  async onNotificationTypeChange() {
    await this.checkIfUniqueKey();
  }

  async onNotificationEventChange() {
    if (this.notificationTemplate.notificationEvent == null) {
      return;
    }
    await this.checkIfUniqueKey();

    // Filter query types so they are unique for the event
    // No longer used - but maybe later...
    // const templatesWithSameEvent = this.templates?.filter(x => x.id != this.notificationTemplate.id &&
    //   x.notificationEventId == this.notificationTemplate.notificationEventId && x.accountId == this.notificationTemplate.accountId);
    // const existingTypes = templatesWithSameEvent?.map(x => x.notificationTypeId);

    this.getContextMenuItems();
    this.getImageMenuItems();
  }

  async checkIfUniqueKey() {
    if (this.isFreeForm) {
      this.isKeyUnique = await this.dbQueryService.checkIfIsUnique(
        this.notificationTemplate,
        "id",
        "templateName",
        "accountId"
      );
      if (!this.isKeyUnique) {
        this.domainService.toastService.error(
          "This Template Name for this account has already been taken.",
          "Unable to use this name"
        );
        return false;
      }
    } else {
      this.isKeyUnique = await this.dbQueryService.checkIfIsUnique(
        this.notificationTemplate,
        "id",
        "notificationEventId",
        "notificationTypeId",
        "accountId"
      );
      if (!this.isKeyUnique) {
        this.domainService.toastService.error(
          "A template for this notification event and type for this account has already been created.",
          "Unable to use this event"
        );
        return false;
      }
    }
    return true;
  }

  private getContextMenuItems() {
    // Get the template variables for the event's query - use either the notificationQueryId or AccountUser for free form context.
    const queryId =
      this.notificationTemplate.notificationEvent?.notificationQueryId;
    this.templatesService.getNotificationMenuItems(queryId).then((x) => {
      this.contextMenuItems = x;
    });
  }

  /** Get the image menu items for the event's query - plus an additional item for selecting a SystemImage from the gallery */
  private getImageMenuItems() {
    const queryId = this.notificationTemplate.notificationEvent?.notificationQueryId;
    this.templatesService.getNotificationMenuItems(queryId).then((items) => {
      const imageItems = items.filter(x => x.automationId.toLowerCase().includes('image')) as MenuItem[];
      const imgRoot = environment.apiRoot + 'img/';
      for (const item of imageItems) {
        // process menu entries to add image url
        const pt = item.automationId.toLowerCase().indexOf("image");
        // extract path, e.g. 'manufacturerImageId' => 'manufacturer' 
        const path = pt == 0 ? 'image' : item.automationId.substring(0, pt);
        item.title = imgRoot + path + '/{{' + item.automationId + '}}';
      }
      // entry for custom images
      imageItems.push({ automationId: 'select', label: 'System Image', title: imgRoot + 'system/' });
      this.imageMenuItems = imageItems;
    });
  }

  /** Select/add a SystemImage from the file gallery, or just configure an image url from the MenuItem's title.
   * This function is called when selecting an item from the image menu of the HtmlEditor */
  async selectImage(item: MenuItem) {
    let url = item.title;
    let config: any = { url };
    if (item.automationId == 'select') {
      // select an image from the gallery
      const img = await this.imageHandler.openFileViewDialog(true);
      if (img) {
        await this.saveImages();
        url += img.systemImage.id;
        config = { url, width: img.width, height: img.height }
      } else {
        return;
      }
    }
    config = await ImageInsertDialogComponent.open(ImageInsertDialogComponent, this, config);
    return config;
  }

  /** Save all changed Image and SystemImage entities */
  async saveImages() {
    await this.imageHandler.attachFiles();
    const imageChanges = this.dbQueryService.uow.getChanges().filter(x => x.entityType.shortName == 'Image' || x.entityType.shortName == 'SystemImage');
    await this.dbQueryService.uow.commit(imageChanges);
    this.oldImages = this.dbQueryService.uow.getEntities(SystemImage);
  }

  /** Load all SystemImages */
  private async loadSystemImages() {
    if (!this.oldImages || !this.oldImages.length) {
      this.oldImages = await this.domainService.dbQueryService.getSystemImages();
    }
  }

  /** Create the join entity between Image and parent entity */
  createImageJoinEntity(event: { imageId: number; isPrimary: boolean }): void {
    this.dbQueryService.uow.createEntity(SystemImage, {
      imageId: event.imageId,
      isPrimary: event.isPrimary,
    });
  }

  async handleBreezeValidationErrors(errEntities: Entity[]) {
    let vErrors = [];
    for (const e of errEntities) {
      vErrors = vErrors.concat(e.entityAspect.getValidationErrors());
    }
    const errMsg = vErrors.map((ve) => ve.errorMessage).join("<br>");
    await this.domainService.dialogService.showOkMessage(
      "Validation Error(s)",
      errMsg
    );
    return false;
  }

  navigateToValidationError(ee: EntityError, event: MouseEvent) {
    this.domainService.toastService.warning(
      "Navigation to error not implemented",
      "Not yet implemented"
    );
  }

  onCancel() {
    this.notificationTemplate.entityAspect.rejectChanges();
    this.ref.close(false);
  }

  hasChanges() {
    return this.notificationTemplate.entityAspect.entityState.isAddedModifiedOrDeleted();
  }

  canPreview() {
    // const user = this.domainService.authService.getUser();
    // return user?.accountId && this.messageTemplate.template && this.messageTemplate.template.includes('data-type="mention"');
    return !!this.notificationTemplate.template;
  }

  isEmail() {
    return (
      this.notificationTemplate.notificationTypeId == NotificationTypeEnum.Email
    );
  }

  isSms() {
    return (
      this.notificationTemplate.notificationTypeId == NotificationTypeEnum.SMS
    );
  }

  messageType() {
    return NotificationTypeEnum[this.notificationTemplate.notificationTypeId];
  }

  canSendTest() {
    return (
      this.notificationTemplate.notificationEventId != null &&
      this.notificationTemplate.notificationTypeId !== null &&
      this.notificationTemplate.template
    );
  }

  getPngDialogService() {
    return this.domainService.pngDialogService;
  }

  private async promptForContext(query: NotificationQuery) {
    const ctx: { [key: string]: string | undefined } = {};
    const accountId = this.notificationTemplate.accountId!;

    const parameterNames = query.parameterNames
      ? query.parameterNames.split(",")
      : [];
    for (let pn of parameterNames.map((pn) => pn.trim())) {
      if (pn == "jobOrderId") {
        pn = "joHeaderId";
      }

      if (pn == "accountId") {
        const accts = await EntityFinderDialog.open(
          AccountFinderDialogComponent,
          this
        );
        if (!accts?.length) {
          return;
        }
        ctx[pn] = "" + _.first(accts)?.id;
      }

      if (pn == "joHeaderId") {
        const jos = await EntityFinderDialog.open(
          JobOrderFinderDialogComponent,
          this
        );
        if (!jos?.length) {
          return;
        }
        ctx[pn] = "" + _.first(jos)?.id;
      }

      if (pn == "numberOfDays") {
        const numberOfDays =
          await this.domainService.dialogService.textpromptDialog(
            "Enter number of days",
            { text: "0", pretext: "" }
          );
        if (numberOfDays === undefined) {
          return;
        }
        ctx[pn] = numberOfDays;
      }

      if (pn == "startDate") {
        const date = await this.domainService.dialogService.textpromptDialog(
          "Enter start date",
          {
            text: this.startDate ?? "",
            pretext: "format MM/DD/YYYY",
          }
        );
        if (!date) {
          return;
        }
        ctx[pn] = date;
      }

      if (!ctx[pn]) {
        const text = await this.domainService.dialogService.textpromptDialog(
          `Enter ${pn}`,
          { text: "", pretext: "" }
        );
        if (!text) {
          return;
        }
        ctx[pn] = text;
      }
    }

    if (Object.keys(ctx).length !== parameterNames.length) {
      await this.domainService.dialogService.okDialog(
        "Unable to preview",
        "Not all parameters could be resolved"
      );
      return null;
    }

    return JSON.stringify(ctx);
  }

  getNotificationQuery() {
    const ne = this.notificationTemplate.notificationEvent;
    if (ne != null) {
      return ne.notificationQuery;
    } else {
      return this.namedNotificationQuery!;
    }
  }

  async onPreview() {
    const query = this.getNotificationQuery();
    const json = await this.promptForContext(query);
    if (!json) {
      return;
    }

    const params: TemplatePreviewDialogParams = {
      template: this.notificationTemplate.template,
      isText: this.isSms(),
      queryId: query.id,
      context: json,
    };
    // strange pathing...
    TemplatePreviewDialogComponent.openDialog(
      this.domainService.pngDialogService,
      params
    );
  }

  async onSendNotification() {
    const query = this.getNotificationQuery();
    const json = await this.promptForContext(query);
    if (!json) {
      return;
    }

    if (
      this.notificationTemplate.notificationTypeId == NotificationTypeEnum.Email
    ) {
      this.emailAddress =
        await this.domainService.dialogService.textpromptDialog(
          "Enter email address",
          {
            text: this.emailAddress ?? "",
            posttext: "Sending takes up to 60 seconds.  Check junk folder.",
          }
        );
      if (!this.emailAddress) {
        return;
      }
    } else if (
      this.notificationTemplate.notificationTypeId == NotificationTypeEnum.SMS
    ) {
      this.phone = await this.domainService.dialogService.textpromptDialog(
        "Enter phone number",
        {
          text: this.phone ?? "",
          pretext: "Phone number starting with +1",
        }
      );
      if (!this.phone) {
        return;
      }
    }

    const user = {
      firstName: "Prox",
      lastName: "Tester",
      email: this.emailAddress ?? "",
      cellNumber: this.phone,
      userName: "proxtester",
    };
    const body = {
      notificationEventId: this.notificationTemplate.notificationEventId,
      notificationTypeId: this.notificationTemplate.notificationTypeId,
      user,
      template: this.notificationTemplate.template,
      contextJson: json,
    };
    const url = environment.entitiesApiRoot + "/SendNotification";

    try {
      const response = await firstValueFrom(
        this.httpClient.post<{ Value: string }>(url, body)
      );

      await this.domainService.dialogService.okDialog(
        "Notification Status",
        response.Value
      );
    } catch (ex) {
      await this.domainService.dialogService.okDialog(
        "Notification Status",
        "Error sending: " + UtilFns.getErrorMessage(ex)
      );
    }
  }
}
